import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Code2, Layers, Cpu, GitBranch, Zap, Shield, Terminal, Sparkles } from 'lucide-react';

const features = [
  { icon: Code2, title: "Multi-Language Code Gen", desc: "Generate production-ready code for web, mobile, APIs, and AI agents" },
  { icon: Layers, title: "Full SDLC Coverage", desc: "From requirements to deployment with RSD, FSD, tests, and docs" },
  { icon: GitBranch, title: "GitHub Integration", desc: "Connect existing repos, analyze codebases, and extend projects" },
  { icon: Cpu, title: "Multiple LLM Models", desc: "Choose from OpenAI, Anthropic, Gemini and more" },
  { icon: Zap, title: "Token-Based Usage", desc: "Pay only for what you use with flexible token packages" },
  { icon: Shield, title: "Enterprise Ready", desc: "Admin controls, usage logs, and team management" },
];

const projectTypes = [
  { name: "Full Stack App", desc: "React, Node, Python, or any stack", color: "from-indigo-500/20 to-transparent" },
  { name: "Mobile App", desc: "iOS, Android, React Native, Flutter", color: "from-cyan-500/20 to-transparent" },
  { name: "API Service", desc: "REST, GraphQL, gRPC backends", color: "from-emerald-500/20 to-transparent" },
  { name: "AI Agent", desc: "Tools, agents, and pipelines", color: "from-purple-500/20 to-transparent" },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-black/40 border-b border-white/10" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="w-6 h-6 text-indigo-400" />
            <span className="text-lg font-bold tracking-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>BuilderShift.ai</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-white/70 hover:text-white" onClick={() => navigate('/login')} data-testid="nav-login-btn">
              Log In
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_0_20px_rgba(99,102,241,0.3)]" onClick={() => navigate('/register')} data-testid="nav-register-btn">
              Get Started <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6" data-testid="hero-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.15)_0%,rgba(5,5,5,0)_50%)]" />
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 mb-6 px-4 py-1.5" data-testid="hero-badge">
            <Sparkles className="w-3 h-3 mr-1.5" /> AI-Powered Development Platform
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Build Software<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">10x Faster</span>
          </h1>
          <p className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto mb-10 leading-relaxed">
            From requirements to production code. BuilderShift.ai handles the entire SDLC cycle with AI-powered code generation, testing, and documentation.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-500 text-white h-12 px-8 text-base shadow-[0_0_30px_rgba(99,102,241,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-all" onClick={() => navigate('/register')} data-testid="hero-cta-btn">
              Start Building Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white/20 hover:border-white/40 text-white h-12 px-8 text-base" onClick={() => navigate('/login')} data-testid="hero-login-btn">
              <Terminal className="w-5 h-5 mr-2" /> Open Console
            </Button>
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20 px-6" data-testid="project-types-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm font-medium tracking-widest uppercase text-indigo-400/60 mb-3 text-center">What You Can Build</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-center mb-16" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Any Project, Any Stack
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {projectTypes.map((type, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg p-6 hover:border-white/20 hover:-translate-y-1 transition-all duration-300" data-testid={`project-type-card-${i}`}>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center mb-4`}>
                  <Code2 className="w-6 h-6 text-white/80" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{type.name}</h3>
                <p className="text-sm text-white/40">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 border-t border-white/5" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm font-medium tracking-widest uppercase text-cyan-400/60 mb-3 text-center">Platform Features</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-center mb-16" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Everything You Need
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feat, i) => (
              <div key={i} className="group bg-white/[0.03] border border-white/10 rounded-lg p-6 hover:border-indigo-500/30 transition-colors" data-testid={`feature-card-${i}`}>
                <feat.icon className="w-8 h-8 text-indigo-400 mb-4 group-hover:text-cyan-400 transition-colors" />
                <h3 className="text-lg font-semibold mb-2">{feat.title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Ready to Ship Faster?
          </h2>
          <p className="text-lg text-white/40 mb-8">Start with 100 free tokens. No credit card required.</p>
          <Button size="lg" className="bg-indigo-600 hover:bg-indigo-500 text-white h-12 px-10 text-base shadow-[0_0_30px_rgba(99,102,241,0.4)]" onClick={() => navigate('/register')} data-testid="cta-register-btn">
            Create Free Account <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-white/30">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            <span>BuilderShift.ai</span>
          </div>
          <span>2026 All rights reserved</span>
        </div>
      </footer>
    </div>
  );
}
